import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Badge, Button, Card, CardBody, CardHeader, Col, Row, Spinner, Table, Tooltip} from "reactstrap";
import {apiErrorRaised, useApiErrorDispatch} from "../../context/api_error/ApiErrorIndex";
import {FeedExports} from "../../services/api/FeedExports";
import FeedExportDeleteButton from "./FeedExportDeleteButton";
import googleImage from "../../assets/img/icons/common/google.png";
import bingImage from "../../assets/img/icons/common/bing.png";
import facebookImage from "../../assets/img/icons/common/facebook.png";
import awinImage from "../../assets/img/icons/common/awin.png";
import googleLocalInventoryImage from "../../assets/img/icons/common/googleLocalInventory.png";
import pinterestImage from "../../assets/img/icons/common/pinterest.png";
import tradeDoublerImage from "../../assets/img/icons/common/doubler.png";
import criteoImage from "../../assets/img/icons/common/criteo.png";
import googleStudioImage from "../../assets/img/icons/common/google_studio.png";
import tiktokImage from "../../assets/img/icons/common/tiktok.png";
import customFeedImage from "../../assets/img/icons/common/custom.png";
import metaLocalizedCatalogsImage from "../../assets/img/icons/common/metaLocalizedCatalogs.png";

import FeedExportHistoryChart from "./FeedExportHistorys/FeedExportHistoryChart";
import {FeedExportStepButtonGroup} from "./FeedExportStepButtonGroup/FeedExportStepButtonGroup";
import {Link} from "react-router-dom";
import {useProjectState} from "../../context/project/ProjectIndex";
import {closeModal, openModal} from "../../context/modal/ModalActions";
import {useModalDispatch} from "../../context/modal/ModalContext";
import {ScheduleFormatter} from "../Schedule";

const FeedExportDetail = ({companyId, projectId, id}) => {
  const projectState = useProjectState();
  const apiErrorDispatch = useApiErrorDispatch();
  const dispatch = useApiErrorDispatch();
  const [isLoading, setLoading] = useState(false);
  const [isRequestExportLoading, setRequestExportLoading] = useState(false);
  const [feedExport, setFeedExport] = useState({});
  const [feedExportPlatformImage, setFeedExportPlatformImage] = useState('');
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const urlRef = useRef(null);
  const modalDispatcher = useModalDispatch();

  const handleRequestExportClick = useCallback(() => {
    openModal(modalDispatcher, {
      title: 'Request export',
      message: `Do you want to generate feed export: ${feedExport.name}?`,
      onConfirm: () => {
        setRequestExportLoading(true);
        FeedExports.requestExport(companyId, projectId, id).then((r) => {
          setRequestExportLoading(false);
          if (r === undefined || r.status > 399) {
            apiErrorRaised(dispatch, r?.response);
          }
        });
        closeModal(modalDispatcher);
      },
      onCancel: () => closeModal(modalDispatcher),
      onBackdropClick: () => closeModal(modalDispatcher),
      buttonText: 'Yes'
    });
  }, [feedExport, companyId, projectId, id, dispatch, modalDispatcher]);

  const handleToggle = useCallback((e) => {
    const {name, checked} = e.target;
    if (checked) {
      FeedExports.activate(companyId, projectId, feedExport.id).then((r) => {
        if (r?.status < 400) {
          setFeedExport(prevFeedExport => ({...prevFeedExport, [name]: checked}));
        } else {
          apiErrorRaised(apiErrorDispatch, r?.response);
        }
      });
    } else {
      FeedExports.disable(companyId, projectId, feedExport.id).then((r) => {
        if (r?.status < 400) {
          setFeedExport(prevFeedExport => ({...prevFeedExport, [name]: checked}));
        } else {
          apiErrorRaised(apiErrorDispatch, r?.response);
        }
      });
    }
  }, [companyId, projectId, feedExport.id, apiErrorDispatch]);

  const handleCopyUrl = useCallback(() => {
    const url = process.env.REACT_APP_CDN_URL + 'feeds/' + projectState.projectId + '/' + feedExport.filename + '.' + feedExport.format;
    navigator.clipboard.writeText(url).then(() => {
      setTooltipOpen(true);
      setTimeout(() => setTooltipOpen(false), 1500);
    });
  }, [feedExport.filename, feedExport.format, projectState.projectId]);

  useEffect(() => {
    setLoading(true);
    FeedExports.get(companyId, projectId, id).then((r) => {
      setLoading(false);
      if (r?.status < 400) {
        r.data.url = process.env.REACT_APP_CDN_URL + 'feeds/' + projectState.projectId + '/' + r.data.filename + '.' + r.data.format;
        setFeedExport(feedExportFromData(r.data));
      } else {
        apiErrorRaised(apiErrorDispatch, r?.response);
      }
    });
  }, [companyId, projectId, id, projectState.projectId, apiErrorDispatch]);

  function feedExportFromData(data) {
    data.url = process.env.REACT_APP_CDN_URL + 'feeds/' + projectState.projectId + '/' + data.filename + '.' + data.format
    if (data.last_time === null || data.last_time === undefined) {
      data = {
        ...data,
        lastDay: '',
        lastHour: '',
      }
    } else {
      let d = new Date(data.last_time)
      data = {
        ...data,
        lastDay: d.toLocaleDateString(projectState.locale.replace('_', '-'), {timeZone: projectState.timezone}),
        lastHour: d.toLocaleTimeString(projectState.locale.replace('_', '-'), {timeZone: projectState.timezone}),
      }
    }
    return data
  }

  useEffect(() => {
    switch (feedExport?.mapping?.platform) {
      case 'google':
        setFeedExportPlatformImage(googleImage);
        break;
      case 'bing':
        setFeedExportPlatformImage(bingImage);
        break;
      case 'facebook':
        setFeedExportPlatformImage(facebookImage);
        break;
      case 'metaLocalizedCatalogs':
        setFeedExportPlatformImage(metaLocalizedCatalogsImage);
        break;
      case 'googleLocalInventory':
        setFeedExportPlatformImage(googleLocalInventoryImage);
        break;
      case 'pinterest':
        setFeedExportPlatformImage(pinterestImage);
        break;
      case 'tradedoubler':
        setFeedExportPlatformImage(tradeDoublerImage);
        break;
      case 'awin':
        setFeedExportPlatformImage(awinImage);
        break;
      case 'criteo':
        setFeedExportPlatformImage(criteoImage);
        break;
      case 'googleStudio':
        setFeedExportPlatformImage(googleStudioImage);
        break;
      case 'tiktok':
        setFeedExportPlatformImage(tiktokImage);
        break;
      case 'custom':
        setFeedExportPlatformImage(customFeedImage);
        break;
    }
  }, [feedExport]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '400px'}}>
        <Spinner color="primary"/>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex flex-column">
        <div className="d-flex flex-column mb-4">
          <div className="d-flex flex-row justify-content-between mb-2">
            <div className="d-flex flex-row align-items-center">
              <Link
                to={`/admin/companies/${projectState.companyId}/projects/${projectState.projectId}/feed_exports/`}>
                <Button className="btn-icon-only text-dark">
                  <span className="fa fa-arrow-left"/>
                </Button>
              </Link>
              <h1 className="ms-4 mb-0">Feed Export Details</h1>
            </div>
            <div className="d-flex align-items-center">
              <FeedExportStepButtonGroup id={id} step={'finish'}/>
            </div>
          </div>
          <div className="d-flex justify-content-end mt-2">
            <a href={feedExport?.url} download target="_blank" rel="noreferrer" className="me-2">
              <Button className="btn-icon" color="primary">
                <span className="me-1 fa fa-download"/>Download
              </Button>
            </a>
            <Button
              onClick={handleRequestExportClick}
              color="primary"
              className="btn-icon me-2"
            >
              {isRequestExportLoading && <span className="spinner-border spinner-border-sm me-1"/>}
              <span className="me-1 fa fa-file-export"/>Export
            </Button>
            <FeedExportDeleteButton id={id}/>
          </div>
        </div>

        <Card className="shadow mb-4">
          <CardHeader className="bg-white py-3">
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0">{feedExport.name}</h3>
              <div className="d-flex align-items-center">
                <label className="custom-toggle me-3 mb-0">
                  <input type="checkbox" name="status" checked={feedExport.status} onChange={handleToggle}/>
                  <span className="custom-toggle-slider rounded-circle"/>
                </label>
                <Badge className={'p-2 pe-3 ps-3'} color={feedExport.status === true ? "success" : "warning"} pill>
                  {feedExport.status === true ? 'ACTIVE' : 'PAUSED'}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <Row>
              <Col>

                <div className="mb-3 text-center">
                  {feedExportPlatformImage && (
                    <img
                      className="platform-image img-fluid mb-3"
                      alt="Platform"
                      style={{maxWidth: '390px'}}
                      src={feedExportPlatformImage}
                    />
                  )}
                </div>
              </Col>
            </Row>
            <Row className="mb-4">
              <Col lg="6" className="mb-3 mb-lg-0">
                <Card className="h-100 border">
                  <CardHeader className="">
                    <h5 className="mb-0">Feed Information</h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <small className="text-uppercase text-muted font-weight-bold d-block mb-1">URL</small>
                      <div className="d-flex align-items-center">
                        <p className="mb-0 me-2 text-truncate" ref={urlRef}
                           style={{maxWidth: '90%'}}>
                          {process.env.REACT_APP_CDN_URL + 'feeds/' + projectState.projectId + '/' + feedExport.filename + '.' + feedExport.format}
                        </p>
                        <span id="feed-url-tooltip"
                              className="fa fa-copy cursor-pointer text-primary"
                              onClick={handleCopyUrl}
                              style={{cursor: 'pointer'}}
                        />
                        <Tooltip
                          placement="top"
                          target="feed-url-tooltip"
                          trigger="manual"
                          isOpen={tooltipOpen}
                        >
                          Copied
                        </Tooltip>
                      </div>
                    </div>
                    <div className="mb-3">
                      <small className="text-uppercase text-muted font-weight-bold d-block mb-1">Products
                        Exported</small>
                      <div className="d-flex align-items-center">
                        <p className="mb-0 me-2 text-truncate" ref={urlRef}
                           style={{maxWidth: '90%'}}>
                          {Intl.NumberFormat(projectState.locale.replace('_', '-')).format(feedExport.last_exported) + ' '}
                          <small>({Intl.NumberFormat(projectState.locale.replace('_', '-')).format(feedExport.last_total)})</small>
                        </p>
                      </div>
                    </div>
                    <div className="mb-3">
                      <small className="text-uppercase text-muted font-weight-bold d-block mb-1">Last
                        Exported</small>
                      <div className="d-flex align-items-center">
                        <div className={'d-flex flex-column justify-content-between'}>
                          <div className={'d-flex flex-row justify-content-center'}>
                            <span className={'fa fa-calendar fa-regular pe-2 pt-1'}/>
                            <span>{feedExport.lastDay}</span>
                          </div>
                          <div className={'d-flex flex-row justify-content-center'}>
                            <span className={'fa fa-clock fa-regular pe-2 pt-1'}/>
                            <span>{feedExport.lastHour}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <small className="text-uppercase text-muted font-weight-bold d-block mb-1">Schedule</small>
                      {feedExport?.schedule ? (
                        <p className={'font-weight-900'}>
                          <ScheduleFormatter schedule={feedExport.schedule}/>
                        </p>
                      ) : (
                        <div className="text-center py-4">No schedule configured</div>
                      )}
                    </div>
                    {
                      feedExport?.mapping?.mapping && feedExport.mapping.mapping.length > 0 ? (
                        <Table className="align-items-center mb-0" responsive>
                          <thead className="thead-light">
                          <tr>
                            <th scope="col" className="ps-3">Origin</th>
                            <th scope="col">Destination</th>
                            <th scope="col">Format</th>
                          </tr>
                          </thead>
                          <tbody>
                          {feedExport.mapping.mapping.map((item, key) => (
                            <tr key={key}>
                              <td className="ps-3">{item.origin}</td>
                              <td>{item.destination}</td>
                              <td>{item.format}</td>
                            </tr>
                          ))}
                          </tbody>
                        </Table>
                      ) : (
                        <div className="text-center py-4">No mapping available</div>
                      )}
                    <Card className="border mt-4">
                      <CardHeader className="">
                        <h5 className="mb-0">URL Parameters</h5>
                      </CardHeader>
                      <CardBody>
                        {
                          feedExport?.url_parameters && Object.keys(feedExport.url_parameters).length > 0 && (
                            <pre className="mb-0" style={{fontSize: '0.875rem'}}>
                              {JSON.stringify(feedExport?.url_parameters, null, 2)}
                            </pre>
                          )
                        }
                      </CardBody>
                    </Card>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="6">
                <Card className="h-100 border">
                  <CardHeader className="">
                    <h5 className="mb-0">Feed Export History</h5>
                  </CardHeader>
                  <CardBody>
                    <FeedExportHistoryChart
                      id={id}
                      companyId={companyId}
                      projectId={projectId}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </div>
    </>
  );
}

export default FeedExportDetail;

